import { CommentPopover } from "@/components/comments/comment-popover";
import { MultiSelectionContextMenu } from "@/components/canvas/multi-selection-context-menu";
import { PermissionCardOverlay } from "@/components/layout/permission-card-overlay";
import { PresentationChrome } from "@/components/focus/presentation-chrome";
import type { CursorInputContextValue } from "@/components/layout/cursor-input-context";
import { CursorInputProvider } from "@/components/layout/cursor-input-context";
import { InteractiveLayerProvider } from "@/components/layout/interactive-layer";
import { MasterSideChrome } from "@/components/layout/master-side-chrome";
import { openMemoryNode } from "@/components/layout/memory-node-navigation";
import {
  WorkspaceToolsProvider,
  type WorkspaceTools,
} from "@/components/layout/workspace-tools-context";
import { OnboardingOverlay } from "@/components/onboarding/onboarding-overlay";
import { AppHeader } from "@/components/panels/app-header";
import type { InputBarProvider } from "@/components/panels/input-bar-model-pill";
import { SearchHub } from "@/components/panels/search-modal";
import { SettingsPocket } from "@/components/settings/settings-pocket";
import { ToastStack } from "@/components/toasts/toast-stack";
import { useDenkerComposerAgent } from "@/hooks/use-denker-composer-agent";
import { useInputModelActions } from "@/hooks/use-input-model-actions";
import { cn } from "@/lib/cn";
import { openGlobalSearch } from "@/lib/global-search";
import { isTauri } from "@/lib/is-tauri";
import type { RuntimeKey } from "@/lib/model-registry";
import { isRuntimeKey } from "@/lib/model-registry";
import { selectProjectFromUser } from "@/lib/select-project-space";
import { useAgentStore } from "@/stores/agent-store";
import { useCompanionStore } from "@/stores/companion-store";
import { useProjectStore } from "@/stores/project-store";
import { useWorkspaceViewStore } from "@/stores/workspace-view-store";
import type { ReactNode } from "react";
import { useCallback, useMemo } from "react";

import { CompactCursorInputOverlay } from "@/desktop/compact-cursor-input-overlay";
import { WorkspaceConversationOverlaySurface } from "@/desktop/workspace-conversation-overlay";
import { useMasterCursorInputSubmit } from "./use-master-cursor-input-submit";
import { useMasterLayoutMedia } from "./use-master-layout-media";
import { useMasterLayoutNavigation } from "./use-master-layout-navigation";
import { useMasterLayoutShell } from "./use-master-layout-shell";
import { useMasterSlashCommands } from "./use-master-slash-commands";

/** Master layout — workspace shell: header, sidebar and canvas overlays. */
interface MasterLayoutProps {
  children?: ReactNode;
}

export function MasterLayout({ children }: MasterLayoutProps) {
  const shell = useMasterLayoutShell();
  const {
    user,
    logout,
    layout,
    data,
    switcherContextMenus,
    maxParallelAgents,
    setMaxParallelAgents,
    attachedImages,
    setAttachedImages,
    isDesktopTauri,
    showAppHeaderChrome,
    showCanvasLeftRail,
    showCenterHeaderChrome,
    showDesktopCanvasContent,
    showLeftHeaderIsland,
    showRightHeaderIsland,
    inputBarDisabled,
    currentProjectId,
    searchRequest,
    searchOpenId,
    closeSearch,
    railPanModeActive,
    railPanModeLabel,
    notePlacementActive,
    presentationVariant,
    reviewChromeActive,
    handleTaskboard,
    handleTerminal,
    handleNote,
    handleReplayTour,
    handleFixCompanion,
    handleZoomIn,
    handleZoomOut,
    handleRailPanMode,
    handleOpenAgentSettings,
    handleOpenSettingsForAgent,
    handleRemoveAgent,
    handleOpenSettingsForAgentName,
    currentSpaceId,
  } = shell;

  const media = useMasterLayoutMedia({
    currentSpaceId,
    setAttachedImages,
  });
  const {
    isStreaming,
    handleStopExecution,
    handleImageFile,
    screenshotIntent,
    setScreenshotIntent,
    handleRemoveImage,
  } = media;

  const navigation = useMasterLayoutNavigation(shell, media);
  const {
    hubNavigation,
    panToFrame,
    agentTemplates,
    projectAgents,
    skillRegistry,
    resolveModelForCapture,
    summonSelectionPresent,
    runTidy,
    handleFitView,
    handleFocusMode,
    handleReviewMode,
  } = navigation;

  const slashCommands = useMasterSlashCommands({
    shell,
    panToFrame,
    runTidy,
    handleFocusMode,
    handleReviewMode,
    projectAgents,
    agentTemplates,
    skillRegistry,
  });

  const handleCursorInputSubmit = useMasterCursorInputSubmit({
    shell,
    media,
    resolveModelForCapture,
    summonSelectionPresent,
  });

  const userLetter =
    user?.name?.[0]?.toUpperCase() ?? user?.email?.[0]?.toUpperCase() ?? "U";

  /* ---------- Input bar status strip: focused agent + providers + update handlers ---------- */

  const { focusedAgent, getFocusedAgentInfo } = useDenkerComposerAgent(
    projectAgents,
    agentTemplates,
    currentSpaceId,
  );

  const availableRuntimes = useCompanionStore((s) => s.availableRuntimes);
  const onWall = useWorkspaceViewStore((s) => s.preferred === "wall");
  // All known runtimes are rendered as tabs so missing CLIs stay
  // discoverable (e.g. Codex not on the launchd PATH used to silently
  // disappear from the picker). Detected runtimes carry their real login
  // state; undetected ones are disabled with an "install" hint surfaced
  // by ModelPickerPopover.
  const popoverProviders = useMemo<InputBarProvider[]>(() => {
    const byRuntimeId = new Map<string, InputBarProvider>();
    for (const rt of availableRuntimes) {
      if (!isRuntimeKey(rt.type)) continue;
      const runtimeId =
        rt.runtime_id ??
        (rt.managed && rt.source ? `${rt.type}:${rt.source}` : rt.type);
      if (byRuntimeId.has(runtimeId)) continue;
      byRuntimeId.set(runtimeId, {
        type: rt.type,
        runtimeId,
        loggedIn: rt.logged_in ?? null,
        detected: true,
        loginCommand: rt.login_command,
        managed: rt.managed,
        source: rt.source,
      });
    }
    const allRuntimes: RuntimeKey[] = [
      "claude_cli",
      "codex_cli",
      "opencode_cli",
    ];
    const baseProviders = allRuntimes.map(
      (type) =>
        byRuntimeId.get(type) ?? {
          type,
          runtimeId: type,
          loggedIn: false,
          detected: false,
        },
    );
    const extraProviders = [...byRuntimeId.values()].filter(
      (provider) =>
        !allRuntimes.includes(
          (provider.runtimeId ?? provider.type) as RuntimeKey,
        ),
    );
    return [...baseProviders, ...extraProviders];
  }, [availableRuntimes]);

  const projectIdForAgents = data.currentProject?.id;
  // Model + effort edits write to AgentTemplate so the change applies to
  // this agent everywhere it's used.
  // ProjectAgent.project_specific_config is not consulted for model
  // resolution anywhere — see resolveTemplateModel.
  // Resolve the AgentTemplate backing a ProjectAgent id. Falls back to
  // fetchAllTemplates() if the cache is empty (the user can open the pill
  // before the templates store has loaded — without this, the write would
  // silently no-op and revert on next render).
  const resolveTemplateForAgent = useCallback(
    async (agentId: string) => {
      const pa = projectAgents.find((a) => a.id === agentId);
      if (!pa) return null;
      let template = agentTemplates.find((t) => t.id === pa.agent_template);
      if (!template) {
        await useAgentStore.getState().fetchAllTemplates();
        template = useAgentStore
          .getState()
          .templates.find((t) => t.id === pa.agent_template);
      }
      return template ?? null;
    },
    [projectAgents, agentTemplates],
  );

  const { handleUpdateAgentModel, handleUpdateAgentEffort } =
    useInputModelActions(projectIdForAgents, resolveTemplateForAgent);

  const { currentProject, currentSpace, handleSelectFrame } = data;
  const workspaceTools = useMemo<WorkspaceTools>(
    () =>
      currentProject && currentSpace
        ? {
            onAddNote: handleNote,
            onAddTerminal: handleTerminal,
            onAddTaskboard: handleTaskboard,
            onAddMemory: () =>
              openMemoryNode({
                nodeId: null,
                currentProject,
                currentSpace,
                selectFrame: handleSelectFrame,
              }),
          }
        : {},
    [
      currentProject,
      currentSpace,
      handleSelectFrame,
      handleNote,
      handleTerminal,
      handleTaskboard,
    ],
  );

  const cursorInputContextValue: CursorInputContextValue = {
    onSubmit: handleCursorInputSubmit,
    inputAgents: data.inputAgents,
    slashCommands,
    focusedAgentInfo: focusedAgent,
    getFocusedAgentInfo,
    companionDisconnected: inputBarDisabled,
    onFixCompanion: handleFixCompanion,
    attachedImages,
    onAttachImages: (images) =>
      setAttachedImages((current) => [...current, ...images]),
    onImageFile: handleImageFile,
    onRemoveImage: handleRemoveImage,
    providers: popoverProviders,
    onUpdateAgentModel: handleUpdateAgentModel,
    onUpdateAgentEffort: handleUpdateAgentEffort,
    onOpenSettingsForAgent: handleOpenSettingsForAgent,
    onOpenSettingsForAgentName: handleOpenSettingsForAgentName,
    onRemoveAgent: handleRemoveAgent,
    isStreaming,
    onStop: handleStopExecution,
    maxParallelAgents,
    onChangeMaxParallelAgents: setMaxParallelAgents,
    onAgentInspect: () => layout.openSettings({ page: "projects" }),
    selectedAgentIds: data.selectedAgentIds,
    agentAvatarStyleKey: user?.agent_avatar_style ?? "glass",
    onSelectAgents: data.handleSelectAgents,
    onOpenSettings: handleOpenAgentSettings,
    projectId: data.currentProject?.id,
    spaceId: data.currentSpace?.id,
    screenshotIntent: isTauri() ? screenshotIntent : undefined,
    setScreenshotIntent: isTauri() ? setScreenshotIntent : undefined,
  };

  return (
    <InteractiveLayerProvider>
      <div
        className="fixed inset-0 overflow-clip bg-transparent"
        data-testid="master-layout"
      >
        {/* Canvas area — full screen behind everything */}
        <div
          className={cn(
            "absolute inset-0",
            !showDesktopCanvasContent && "pointer-events-none opacity-0",
          )}
          data-testid="canvas-area"
        >
          <CursorInputProvider value={cursorInputContextValue}>
            <WorkspaceToolsProvider value={workspaceTools}>
              {children}
            </WorkspaceToolsProvider>
          </CursorInputProvider>
        </div>

        {/* Desktop shortcut input owns a body portal in every mode so the
            workspace and onboarding card cannot cover or clip it. */}
        {isDesktopTauri && (
          <CompactCursorInputOverlay ctx={cursorInputContextValue} />
        )}

        {isDesktopTauri && (
          <WorkspaceConversationOverlaySurface
            inputContext={cursorInputContextValue}
          />
        )}

        {/* Header — floating, above canvas overlays so right-island controls stay clickable. */}
        {showAppHeaderChrome && (
          <div
            className={cn(
              "pointer-events-none absolute top-0 z-40",
              isDesktopTauri ? "inset-x-3" : "inset-x-0",
            )}
            data-testid="app-header-shell"
          >
            <AppHeader
              showLeftIsland={showLeftHeaderIsland}
              showRightIsland={showRightHeaderIsland}
              showCenterChrome={showCenterHeaderChrome && !reviewChromeActive}
              projectName={data.currentProject?.name ?? "Denker"}
              onRenameProject={
                data.currentProject
                  ? (name: string) =>
                      data.renameProject(data.currentProject!.id, name)
                  : undefined
              }
              switcherProjects={data.switcherProjects}
              onSelectProject={(projectId) => {
                const project = useProjectStore
                  .getState()
                  .projects.find((p) => p.id === projectId);
                if (project) selectProjectFromUser(project);
              }}
              onAddProject={() => data.createProjectWithDefaults()}
              renderProjectContextMenu={
                switcherContextMenus.renderProjectContextMenu
              }
              userLetter={userLetter}
              onSearch={() =>
                openGlobalSearch({ chip: "all", projectId: currentProjectId })
              }
              onShortcuts={() =>
                layout.openSettings({ page: "preferences-shortcuts" })
              }
              onOpenNotifications={() =>
                openGlobalSearch({
                  chip: "notifications",
                  projectId: currentProjectId,
                })
              }
              onSettings={() => layout.openSettings({ page: "root" })}
              onLogout={() => logout()}
              isOnboarding={data.currentSpace?.is_onboarding}
              onReplayTour={handleReplayTour}
              className={isDesktopTauri ? "px-0" : undefined}
            />
          </div>
        )}

        <MasterSideChrome
          data={data}
          layout={layout}
          isDesktopTauri={isDesktopTauri}
          showAppHeaderChrome={showAppHeaderChrome}
          showCanvasLeftRail={showCanvasLeftRail}
          onFitView={handleFitView}
          onZoomIn={handleZoomIn}
          onZoomOut={handleZoomOut}
          onRailPanMode={handleRailPanMode}
          panModeActive={railPanModeActive}
          panModeLabel={railPanModeLabel}
          focusActive={presentationVariant === "focus"}
          onNotePlacement={workspaceTools.onAddNote}
          notePlacementActive={notePlacementActive}
          onTaskboardPlacement={workspaceTools.onAddTaskboard}
          onMemoryPlacement={workspaceTools.onAddMemory}
          onTerminalPlacement={workspaceTools.onAddTerminal}
          onOpenSearch={() =>
            openGlobalSearch({ chip: "all", projectId: currentProjectId })
          }
          onOpenNotifications={() =>
            openGlobalSearch({
              chip: "notifications",
              projectId: currentProjectId,
            })
          }
        />

        {/* Comment popover — fixed position, driven by canvas store */}
        <CommentPopover />

        {/* Desktop toasts live in the always-mounted shell on every display. */}
        {!isTauri() && <ToastStack />}

        {/* Onboarding overlay — web only; desktop renders from DesktopShell */}
        {!isTauri() && <OnboardingOverlay />}

        {/* Directory permission overlay — centered top, above toasts */}
        <PermissionCardOverlay />

        {/* Settings pocket */}
        <SettingsPocket
          open={layout.settingsOpen}
          focusOnOpen={layout.settingsTakesKeyboard}
          stack={layout.settingsStack}
          onStackChange={layout.setSettingsStack}
          onClose={layout.closeSettings}
        />

        {searchRequest ? (
          <SearchHub
            key={searchOpenId}
            request={searchRequest}
            onClose={closeSearch}
            navigation={hubNavigation}
          />
        ) : null}

        {/* Presentation mode behavior layer (keyboard, review panel, selection) */}
        <PresentationChrome />

        {/* The canvas multi-selection menu; the wall has its own. */}
        {onWall ? null : <MultiSelectionContextMenu />}
      </div>
    </InteractiveLayerProvider>
  );
}
